//Buy - Sell

import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../contexts/TenantContext";
import { useURLParams } from "../contexts/URLParamsContext";
import { useGooglePlaces, PlaceResult } from "../hooks/search/useGooglePlaces";
import useContactAddressAutoFill from "../hooks/contacts/useContactAddressAutoFill";
import useContactPropertyFetch from "../hooks/contacts/useContactPropertyFetch";
import { searchByFullAddress } from "../api/valuation";
import { userService } from "../services/userService";
import { webActivityService } from "../services/webActivityService";
import {
  useAuthUser,
  useAuthLoading,
  useIsAuthenticated,
} from "@/hooks/auth/useAuth";
import PropertySqueezeOverlay from "../components/property/valuation/PropertySqueezeOverlay";
import { PropertyValuationData } from "../types/valuation";
import { Search, Loader, MapPin } from "lucide-react";

const ValuationLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { config } = useTenant();
  const { params: urlParams } = useURLParams();
  const user = useAuthUser();
  const authLoading = useAuthLoading();
  const isAuthenticated = useIsAuthenticated();

  if (!config) return null;
  const { colors } = config.branding;

  // Get URL params for initial state
  const urlSearchParams = new URLSearchParams(window.location.search);
  const initialAddress = urlSearchParams.get("requested_address") || "";
  const testSuccess = urlSearchParams.get("test_success") === "true"; // For testing success UI

  const [address, setAddress] = useState(
    initialAddress || (testSuccess ? "123 Test St, Calgary, AB, Canada" : ""),
  );
  const [addressParts, setAddressParts] = useState<
    PlaceResult["addressParts"] | null
  >(null);
  const [propertyPreview, setPropertyPreview] =
    useState<PropertyValuationData | null>(null);
  const [showSqueezeOverlay, setShowSqueezeOverlay] = useState(false);
  const [isCanadianAddress, setIsCanadianAddress] = useState(testSuccess);
  const [showRequestSuccess, setShowRequestSuccess] = useState(testSuccess);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use the contact property fetch hook to get property details when contact_id is present
  const contactPropertyFetch = useContactPropertyFetch({
    contactId: urlParams.contact_id,
    setPropertyDetails: setPropertyPreview,
  });

  // Use the contact address auto-fill hook
  const contactAddressAutoFill = useContactAddressAutoFill({
    contactId: urlParams.contact_id,
    setAddress,
  });

  // Determine if we should show loading state
  const showLoading =
    urlParams.contact_id && (authLoading || contactPropertyFetch.isLoading);

  // Check authentication state, URL parameters, and property fetch state
  useEffect(() => {
    if (!authLoading) {
      // Show squeeze overlay if:
      // 1. Not authenticated
      // 2. Has contact_id
      // 3. UTM source is email
      // Property details don't matter - show overlay regardless
      const shouldShowOverlay =
        !isAuthenticated &&
        !!urlParams.contact_id &&
        urlParams.utm_source === "email";

      setShowSqueezeOverlay(shouldShowOverlay);
    }
  }, [
    isAuthenticated,
    authLoading,
    urlParams.contact_id,
    urlParams.utm_source,
    user,
    contactPropertyFetch.propertyFound,
    propertyPreview,
  ]);

  const handlePlaceSelect = useCallback(
    async (placeResult: { address: string; addressParts: any }) => {
      setAddress(placeResult.address);
      setAddressParts(placeResult.addressParts);
      setError(null);
      // Detect Canadian address
      setIsCanadianAddress(placeResult.addressParts.country === "CA");
    },
    [],
  );

  const trackValuationRequest = useCallback(
    async (submittedAddress: string, metadata: Record<string, any> = {}) => {
      try {
        await webActivityService.trackEvent({
          event_type: "valuation_request",
          event_category: "Conversion",
          event_action: "Home Valuation Request",
          event_label: "home_valuation",
          properties: {
            ...metadata,
            requested_address: submittedAddress,
            contact_id: urlParams.contact_id,
            tenant_id: urlParams.tenant_id,
            source: "valuation_landing_page",
          },
        });
      } catch (err) {
        console.warn("Failed to track valuation request web activity:", err);
      }
    },
    [urlParams.contact_id, urlParams.tenant_id],
  );

  const handleSubmitAddress = async () => {
    if (!address) {
      console.warn("No address entered");
      return;
    }

    setLoading(true);
    setError(null);

    // For Canadian addresses, handle differently since API doesn't support them
    if (isCanadianAddress) {
      // Add address to URL so frontend can capture it for valuationRequested field
      const url = new URL(window.location.href);
      url.searchParams.set("requested_address", address);
      window.history.replaceState({}, "", url.toString());

      await trackValuationRequest(address, {
        country: "CA",
        valuation_result: "manual_follow_up",
      });

      // If already logged in, skip squeeze overlay and append to valuationRequested array
      if (isAuthenticated && user?.id) {
        try {
          await userService.appendValuationRequest(user.id, address);
        } catch (err) {
          console.error("Failed to append valuation request:", err);
        }
        setLoading(false);
        setShowRequestSuccess(true);
      } else {
        setLoading(false);
        setShowSqueezeOverlay(true);
      }
      return;
    }

    try {
      const response = await searchByFullAddress(address);
      if (!response || !response.id) {
        setError(
          "Oops! We're still gathering data for this property! Please try another property. We've got thousands ready for instant valuation!",
        );
        return;
      }
      await trackValuationRequest(address, {
        property_id: response.id,
        valuation_result: "instant_preview",
      });

      // Open in new tab
      navigate(`/property/${response.id}/valuation`);
    } catch (error) {
      console.error("Error fetching property preview:", error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("Unable to fetch property details. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle invalid address selection (e.g., missing house number)
  const handleInvalidAddress = useCallback(
    (message: string, address?: string) => {
      setError(message);
      if (address) {
        setAddress(address);
      }
    },
    [],
  );

  // Pass the callback to useGooglePlaces
  useGooglePlaces(handlePlaceSelect, undefined, handleInvalidAddress);

  // Helper to clean up the requested_address URL parameter
  const cleanupRequestedAddressParam = useCallback(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("requested_address")) {
      url.searchParams.delete("requested_address");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const handleSqueezeSuccess = () => {
    setShowSqueezeOverlay(false);
    // For Canadian addresses, show success message instead of navigating
    if (isCanadianAddress) {
      setShowRequestSuccess(true);
      // Don't clean up URL here - userService needs it for valuationRequested field
      // Cleanup happens when user clicks "Check Another Property"
    }
  };

  const handleSqueezeClose = () => {
    setShowSqueezeOverlay(false);
    // Clean up the URL parameter when closing
    cleanupRequestedAddressParam();
  };

  return (
    <>
      {/* Show loading spinner if we're loading data */}
      {showLoading ? (
        <div className="min-h-screen flex items-center justify-center">
          <Loader className="animate-spin h-10 w-10 text-primary" />
        </div>
      ) : (
        <div
          className="min-h-[calc(100vh-100px)] 3xl:min-h-[calc(100vh-200px)] flex items-center"
          style={{
            backgroundImage: `url('/assets/images/home-value-bg.webp')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div
            className={`w-full relative overflow-hidden transition-all duration-500 py-10 xl:py-20 3xl:py-28 ${
              showSqueezeOverlay ? "blur-md" : ""
            }`}
          >
            <div className="mx-auto px-4 xl:px-6">
              <div className="max-w-6xl mx-auto text-center">
                {showRequestSuccess ? (
                  /* Success State */
                  <div className="text-center py-8 xl:py-12 px-4">
                    <h2 className="text-2xl xl:text-4xl font-bold text-white mb-4">
                      You're All Set! 🎉
                    </h2>

                    <p className="text-white/90 text-base xl:text-lg mb-8 max-w-lg mx-auto leading-relaxed">
                      Thanks for reaching out! Our team will prepare a
                      personalized home valuation and get back to you soon.
                    </p>

                    <div className="mb-8">
                      <div className="inline-block max-w-full px-4 py-3 bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl">
                        <p className="text-sm xl:text-base text-white font-medium">
                          📍 {address}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setShowRequestSuccess(false);
                        setAddress("");
                        setIsCanadianAddress(false);
                        cleanupRequestedAddressParam();
                      }}
                      className="h-12 px-8 bg-white font-semibold rounded-lg shadow-sm btn-primary hover:opacity-90 transition-all duration-300"
                    >
                      Check Another Property
                    </button>
                  </div>
                ) : (
                  /* Normal State - Header + Search Card */
                  <>
                    <h1 className="heading-hero animate-fade-up text-white">
                      <span className="block md:inline text-3xl md:text-4xl xl:text-5xl 3xl:text-6xl font-bold">
                        Discover Your
                      </span>{" "}
                      <span className="block md:inline text-3xl xl:text-5xl 3xl:text-6xl font-black">
                        Property's True Value
                      </span>
                    </h1>
                    <p className="w-full sm:w-[500px] xl:w-[700px] 3xl:w-[950px] mx-auto mt-3 xl:mt-5 hero-subtitle animate-fade-in-delay text-sm xl:text-lg 3xl:text-2xl font-normal 3xl:!leading-10 text-white">
                      Get an instant, accurate valuation of your property with
                      our advanced analytics. Powered by AI and real market data
                      for precise estimates.
                    </p>

                    <div className="mx-auto max-w-3xl xl:max-w-4xl card mt-8 xl:mt-12 bg-white/90 backdrop-blur-sm shadow-xl rounded-2xl p-6 xl:p-8 animate-fade-up">
                      <div className="places-autocomplete space-y-4">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <MapPin
                              className="h-5 w-5"
                              style={{ color: `${colors.primary}99` }}
                            />
                          </div>
                          <input
                            id="address-input"
                            type="text"
                            className="input-field w-full pl-10 pr-4 py-2 3xl:px-4 3xl:py-3 3xl:pl-14 text-sm border rounded-lg transition-colors shadow-sm focus:ring-2 focus:border-primary"
                            style={{
                              ["--tw-ring-color" as string]: "var(--primary)",
                              ["--tw-ring-offset-width" as string]: "0px",
                              ["--tw-ring-width" as string]: "2px",
                            }}
                            placeholder="Enter your property address"
                            value={address}
                            autoFocus
                            onChange={(e) => setAddress(e.target.value)}
                            onFocus={(e) => e.target.select()}
                          />
                        </div>
                        <button
                          className="h-12 flex-shrink-0 w-full text-white font-semibold py-2 3xl:px-6 rounded-lg shadow-sm btn-primary hover:opacity-90 transition-all duration-300 flex items-center justify-center"
                          style={{
                            background: `linear-gradient(135deg, ${colors.primary}, ${colors.primary}dd)`,
                          }}
                          onClick={handleSubmitAddress}
                          disabled={
                            !address ||
                            loading ||
                            contactAddressAutoFill.isLoading
                          }
                        >
                          {loading || contactAddressAutoFill.isLoading ? (
                            <>
                              <Loader className="animate-spin h-5 w-5 text-white" />
                              {contactAddressAutoFill.isLoading && (
                                <span className="ml-2 text-sm lg:text-base py-1">
                                  Loading Address...
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              <span className="text-sm lg:text-base py-1">
                                Get Property Value
                              </span>
                              <Search className="w-5 h-5 ml-2" />
                            </>
                          )}
                        </button>
                      </div>

                      {error && (
                        <div className="text-sm lg:text-base mt-4 p-2 py-4 lg:p-4 bg-red-50 text-red-700 rounded-lg flex items-center ">
                          {error}
                        </div>
                      )}
                    </div>
                    <div className="flex justify-center">
                      <p className="mt-6 text-xs lg:text-sm animate-fade-in-delay text-white max-w-[280px] md:max-w-full">
                        By continuing, you agree to our{" "}
                        <a
                          href="/terms"
                          className="underline text-white transition-colors"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms of Service
                        </a>{" "}
                        and{" "}
                        <a
                          href="/privacy"
                          className="underline text-white transition-colors"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Privacy Policy
                        </a>
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {showSqueezeOverlay && (
            <div className="absolute inset-0 z-50 flex items-center justify-center">
              <PropertySqueezeOverlay
                onClose={handleSqueezeClose}
                onSuccess={handleSqueezeSuccess}
                propertyDetails={propertyPreview}
                isConfirmationMode={
                  !!urlParams.contact_id && urlParams.utm_source === "email"
                }
                requestedAddress={isCanadianAddress ? address : undefined}
                isValuationRequest={isCanadianAddress}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ValuationLandingPage;
